import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import * as variables from '../../styles/variables.module.scss'; // translates our variable names to hex
import { background, bgWrapper } from './PageBg.module.scss';
gsap.registerPlugin(ScrollTrigger);

const PageBg = ({
  bgContextRef, // I was trying to pass a ref from the parent, but it doesn't really work
  colorTrigger,
  sizeTrigger,
  markers,
  initialColor,
}) => {
  // In addition to the props, Trigger elements also need a data-color attribute that uses a color name, like "Midnight"

  const bgWrapperRef = useRef();
  const bgRef = useRef();

  // useLayoutEffect and useGSAP doesn't work here, it must fire too early
  useEffect(() => {
    // Setup a GSAP Context to scope the selectors and make the cleanup function easy
    let bgContext = gsap.context(() => {
      // Hide null warnings that seemed to be onEnterBack getting called after a new page loads
      gsap.config({ nullTargetWarn: false });

      // Fade in the background on load if the page has been reloaded partway down
      gsap.to(bgRef.current, {
        autoAlpha: 1,
        ease: 'power2.inOut',
        duration: 0.5,
        delay: window.scrollY > 100 ? 1 : 0, // the delay is hiding the animation from initialColor to the active color
      });
      // Color Animation
      // Get all the colorTriggers
      let colorTriggers = gsap.utils.toArray(colorTrigger);

      // Loop through the colorTriggers, get their color, and the previous and next colors if they exist
      colorTriggers.forEach(function (trigger, index) {
        const color = trigger.getAttribute('data-color');

        // Create a tween to animate the background
        const colorTween = (color, duration) => {
          gsap.to(bgRef.current, {
            backgroundColor: variables[`color${color}`],
            ease: 'power2.inOut',
            duration: duration,
          });
        };

        // Use ScrollTrigger callbacks to run the tween
        ScrollTrigger.create({
          id: `bg-color-${index}`,
          trigger: trigger,
          start: 'top 65%',
          end: 'bottom 65%',
          // markers: true,
          refreshPriority: -30, // waiting for other scrolltriggers to recalculate
          onToggle: (self) => {
            self.isActive && colorTween(color, 1);
          },
          // This helped page reloads look faster, because it prevented the animation from the initial (usually purple) bakground color
          // But then we found that if an accordion changing height caused a background color change, the color jumped instead of animating
          // onRefresh: (self) => {
          //   self.isActive && colorTween(color, 0);
          // },
        });
      });

      // Size Animation
      // This is a simple version that relies on toggleActions
      // instead of data attributes
      // Get all the sizeTriggers
      let sizeTriggers = gsap.utils.toArray(sizeTrigger);
      const sizeTl = gsap.timeline({});
      sizeTl
        .to(
          bgWrapperRef.current,
          {
            padding: 0,
            duration: 0.5,
            ease: 'power2.inOut',
          },
          '0'
        )
        .to(
          bgRef.current,
          {
            borderRadius: 0,
            duration: 0.5,
            ease: 'power2.inOut',
          },
          '0'
        );

      sizeTriggers.forEach(function (trigger, index) {
        ScrollTrigger.create({
          id: `bg-size-${index}`,
          trigger: trigger,
          start: 'top 65%',
          end: 'bottom 65%',
          animation: sizeTl,
          toggleActions: 'reverse play reverse play',
          refreshPriority: -30,
          // markers: true,
          invalidateOnRefresh: true, // this updates the style values when the window resizes
          onRefresh: (self) => {
            // Force the background to expand if the page is refreshed after scrolling
            self.progress === 1 && self.animation.progress(1);
          },
        }).refresh();
      });

      // Force the background to expand if the page is reloaded between the hero and footer size triggers
      let heroTrigger = ScrollTrigger.getById('bg-size-0');
      let footerTrigger = ScrollTrigger.getById('bg-size-1');
      heroTrigger.progress === 1 &&
        footerTrigger.progress === 0 &&
        sizeTl.progress(1);

      //ScrollTrigger.refresh();
      // bgContextRef was being used for scope, but since refs don't update it caused 'invalid scope'
    });
    return () => {
      // ScrollTrigger.refresh();
      bgContext.revert();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div className={`${bgWrapper}`} ref={bgWrapperRef}>
      <div
        className={`${background} texturedBgVarColor varColor${initialColor}`}
        ref={bgRef}
      ></div>
    </div>
  );
};

PageBg.propTypes = {
  colorTrigger: PropTypes.string,
  sizeTrigger: PropTypes.string,
  markers: PropTypes.bool,
  initialColor: PropTypes.string,
};

export default PageBg;
